import { useCallback, useEffect, useMemo, useState } from "react";

import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";

export interface ExportNovedadesListItem {
    id: string;
    title: string;
    period: string;
    status: string;
    progress: string;
    totals: string;
    fileName: string;
    updatedAt: string;
    raw: NovedadesExportServiceModel.ProcessRecord;
}

export type ExportNovedadesModalMode = "new" | "view";

interface UseExportNovedadesScreenViewModelProps {
    orgID: string;
    timezone?: string;
    service: NovedadesExportService;
}

const statusLabels: Record<string, string> = {
    CREATED: "Creado",
    RUNNING: "Procesando",
    PROCESSING: "Procesando",
    READY_FOR_REVIEW: "Listo para revisar",
    EXPORT_GENERATED: "Exportación generada",
    COMPLETED: "Completado",
    PARTIALLY_FAILED: "Completado con errores",
    FAILED: "Fallido",
};

const formatDate = (date: string | undefined): string => {
    if (!date) {
        return "N/D";
    }

    return date;
};

const formatTimestamp = (timestamp: number | undefined, timezone?: string): string => {
    if (!timestamp) {
        return "N/D";
    }

    return new Date(timestamp).toLocaleString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
    });
};

const buildSearchText = (row: ExportNovedadesListItem): string => {
    return [
        row.title,
        row.period,
        row.status,
        row.fileName,
        row.raw.exportSystem,
        row.raw.calculationProfileID,
    ].filter(Boolean).join(" ").toLowerCase().replace(/\s/g, "");
};

const mapProcessToListItem = (
    process: NovedadesExportServiceModel.ProcessRecord,
    timezone?: string,
): ExportNovedadesListItem => {
    const employeeCount = process.employeeCount ?? 0;
    const processedCount = process.processedEmployeeCount ?? 0;
    const dateRange = `${formatDate(process.dateFrom)} - ${formatDate(process.dateTo)}`;

    return {
        id: process.processID,
        title: process.title || "Exportación sin título",
        period: process.period || dateRange,
        status: statusLabels[process.status] ?? process.status,
        progress: `${processedCount}/${employeeCount}`,
        totals: `${process.errorsCount} errores / ${process.warningsCount} avisos`,
        fileName: process.outputFilename || "Pendiente",
        updatedAt: formatTimestamp(
            process.firestoreUpdateTimestamp ?? process.firestoreCreationTimestamp,
            timezone,
        ),
        raw: process,
    };
};

const processAnalyticsPayload = (process: NovedadesExportServiceModel.ProcessRecord) => ({
    processID: process.processID,
    status: process.status,
    currentStep: process.currentStep,
    exportSystem: process.exportSystem,
    employeeCount: process.employeeCount ?? 0,
    processedEmployeeCount: process.processedEmployeeCount ?? 0,
    errorsCount: process.errorsCount ?? 0,
    warningsCount: process.warningsCount ?? 0,
});

export const useExportNovedadesScreenViewModel = ({
    orgID,
    timezone,
    service,
}: UseExportNovedadesScreenViewModelProps) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchField, setSearchField] = useState("");
    const [exports, setExports] = useState<NovedadesExportServiceModel.ProcessRecord[]>([]);
    const [modalMode, setModalMode] = useState<ExportNovedadesModalMode>("new");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfigurationModalOpen, setIsConfigurationModalOpen] = useState(false);
    const [selectedProcess, setSelectedProcess] = useState<NovedadesExportServiceModel.ProcessRecord | undefined>(undefined);

    const refresh = useCallback(async (): Promise<NovedadesExportServiceModel.ProcessRecord[]> => {
        if (!orgID) {
            setExports([]);
            setLoading(false);
            return [];
        }

        setLoading(true);
        setError(null);

        try {
            const processRecords = await service.listProcesses(orgID);
            setExports(processRecords);
            return processRecords;
        } catch (loadError) {
            console.error(loadError);
            setError("No se pudieron cargar las exportaciones.");
            return [];
        } finally {
            setLoading(false);
        }
    }, [orgID, service]);

    useEffect(() => {
        setSearchField("");
        void refresh();
    }, [refresh]);

    const rows = useMemo(() => {
        return exports
            .map((process) => mapProcessToListItem(process, timezone))
            .sort((a, b) => {
                const updatedA = a.raw.firestoreUpdateTimestamp ?? a.raw.firestoreCreationTimestamp ?? 0;
                const updatedB = b.raw.firestoreUpdateTimestamp ?? b.raw.firestoreCreationTimestamp ?? 0;
                return updatedB - updatedA;
            });
    }, [exports, timezone]);

    const filteredRows = useMemo(() => {
        const normalizedSearch = searchField.toLowerCase().replace(/\s/g, "");

        if (!normalizedSearch) {
            return rows;
        }

        return rows.filter((row) => buildSearchText(row).includes(normalizedSearch));
    }, [rows, searchField]);

    const openCreateExport = useCallback(() => {
        NovedadesExportAnalyticsHelper.trackCreateOpened();
        setModalMode("new");
        setSelectedProcess(undefined);
        setIsModalOpen(true);
    }, []);

    const openProcess = useCallback((process: NovedadesExportServiceModel.ProcessRecord) => {
        NovedadesExportAnalyticsHelper.trackProcessOpened(processAnalyticsPayload(process));
        setSelectedProcess(process);
        setModalMode("view");
        setIsModalOpen(true);
    }, []);

    const handleProcessCreated = useCallback(async (process: NovedadesExportServiceModel.ProcessRecord) => {
        setSelectedProcess(process);
        setModalMode("view");
        setIsModalOpen(true);
        await refresh();
    }, [refresh]);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const openConfigurationModal = useCallback(() => {
        NovedadesExportAnalyticsHelper.trackConfigurationOpened();
        setIsConfigurationModalOpen(true);
    }, []);

    const closeConfigurationModal = useCallback(() => {
        setIsConfigurationModalOpen(false);
    }, []);

    return {
        loading,
        error,
        searchField,
        setSearchField,
        rows,
        filteredRows,
        modalMode,
        isModalOpen,
        isConfigurationModalOpen,
        selectedProcess,
        openCreateExport,
        openProcess,
        openConfigurationModal,
        closeConfigurationModal,
        handleProcessCreated,
        closeModal,
        refresh,
    };
};
